import { useConfigStore } from './useConfigStore'
import { z } from 'zod'

const sponsoredTilesSchema = z.object({
  provider: z.union([
    z.literal('affinity'),
    z.literal('admarketplace'),
    z.literal('off'),
  ]),
  tilesCount: z.number().default(5),
  design: z.union([z.literal('msn'), z.literal('google')]).optional(),
  variant: z
    .union([
      z.literal('default'),
      z.literal('circle'),
      z.literal('large-primary'),
      z.literal('large-secondary'),
    ])
    .optional(),
})

const tilesProviderSchema = z.object({
  provider: z.union([z.literal('affinity'), z.literal('admarketplace')]),
  tilesCount: z.number().default(5),
})

const directTilesProviderSchema = z.object({
  provider: z.literal('direct'),
  name: z.string(),
  image_url: z.string(),
  click_url: z.string(),
  impression_url: z.string(),
})

const dynamicSponsoredTilesSchema = z.object({
  tilesProvider: z.array(
    z.union([tilesProviderSchema, directTilesProviderSchema])
  ),
  design: z.union([z.literal('msn'), z.literal('google')]).optional(),
  variant: z
    .union([
      z.literal('default'),
      z.literal('circle'),
      z.literal('large-primary'),
      z.literal('large-secondary'),
    ])
    .optional(),
})

const dynamicFloorPricingSchema = z.object({
  isEnabled: z.boolean().default(true),
  flooredPlacements: z.array(z.string()).optional().default([]),
})

export const adSettingsSchema = z.object({
  sponsoredTiles: z.optional(sponsoredTilesSchema),
  dynamicSponsoredTiles: z.optional(dynamicSponsoredTilesSchema),
  aboveFoldSectionAds: z.optional(aboveFoldSectionAdsSchema),
  aboveFoldSectionLeaderboardAd: z
    .union([
      z.literal('on_testsection_leader'),
      z.literal('sn_testsection_leader'),
      z.literal('gd_testsection_leader'),
      z.literal(''),
    ])
    .default('')
    .optional(),
  adsDynamicFloorPricing: z.optional(dynamicFloorPricingSchema),
  enableSubtleGreyAdBackground: z.boolean().optional(),
})

export type TilesProvider =
  | z.infer<typeof tilesProviderSchema>
  | z.infer<typeof directTilesProviderSchema>
export type DynamicSponsoredTiles = z.infer<typeof dynamicSponsoredTilesSchema>
export type AdSettings = z.infer<typeof adSettingsSchema>

export const useAdSettingsStore = useConfigStore<AdSettings>({
  defaults: adSettingsSchema.parse({
    sponsoredTiles: {
      provider: 'off',
      tilesCount: 5,
    },
    dynamicSponsoredTiles: undefined,
    aboveFoldSectionAds: undefined,
    aboveFoldSectionLeaderboardAd: '',
    enableSubtleGreyAdBackground: false,
    adsDynamicFloorPricing: {
      isEnabled: false,
      flooredPlacements: [],
    },
  }),
  configStoreKey: 'adSettings',
  schema: (data: unknown) => adSettingsSchema.parse(data),
})
